import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { defaultModelFor } from '../../utils/modelLists';
import { DISABLED_PROVIDER } from '../../utils/userPageHelpers';
import {
  fitConfigToAgentType,
  isSingleCallAgentType,
  slotCopy,
  slotsForAgentType,
} from '../../utils/workflowModelSlots';
import ModelChoicePicker from './ModelChoicePicker';
import ModelSelect from './ModelSelect';
import { Field, FieldAlert, TypeHint } from './workflowFormFields';
import {
  STACKFORM_PROVIDER_OPTION,
  buildConfiguredProviderOptions,
} from './workflowProviderOptions';

/** Node-type accent colours used in headers and badges. */
export const NODE_COLORS = {
  input: '#22c55e',
  output: '#ef4444',
  agent: '#3b82f6',
  tool: '#f97316',
  logic: '#a855f7',
};

const AGENT_TYPES = [
  'generalist',
  'researcher',
  'evaluator',
  'challenger',
  'extractor',
];

const AGENT_DESCRIPTIONS = {
  generalist:
    'All-purpose reasoning and synthesis. Best as the final step to ' +
    'combine search results, draft answers, or explain topics. Runs 3 ' +
    'parallel sub-agents internally and searches the web automatically.',
  researcher:
    'Deep academic and technical research. Always queries arXiv, ' +
    'Tavily, and DuckDuckGo — web search is always on and cannot ' +
    'be disabled. Best for scientific or research-heavy questions ' +
    'where source quality and citations matter.',
  evaluator:
    'Scores and critiques a previous response. Place after a generalist ' +
    'or researcher to grade quality, flag gaps, or produce a structured ' +
    'assessment. Output includes ratings and improvement notes.',
  challenger:
    'Adversarial critique and red-teaming. Feed it a prior response and ' +
    'it surfaces weaknesses, counterarguments, and blind spots. Pair ' +
    'with a generalist: generalist → challenger → output.',
  extractor:
    'Pulls structured JSON from unstructured text. Use after a tool or ' +
    'agent to extract named fields (e.g. title, url, date). Each key in ' +
    'the JSON becomes its own context variable reachable via ' +
    '{{field_name}} in downstream nodes.',
};

const TOOL_TYPES = [
  'ddg_search',
  'arxiv_search',
  'tavily_search',
  'you_search',
];

const TOOL_DESCRIPTIONS = {
  ddg_search:
    'Searches the web via DuckDuckGo. Your query is not sent to ' +
    'Stackform or any third-party provider — DuckDuckGo does not track ' +
    'or profile searches. Use {{query}} in the parameters JSON. Best ' +
    'for current events, news, and general topics.',
  arxiv_search:
    'Searches arXiv for academic papers — titles, abstracts, and ' +
    'authors. Use {{query}} as the search term. Best for scientific and ' +
    'technical subjects.',
  tavily_search:
    'Premium web search via Tavily (requires a Tavily API key in ' +
    'Settings → API Keys). Produces higher-quality, cleaner results than ' +
    'DuckDuckGo. Use {{query}} as the search term.',
  you_search:
    'Premium web and news search via You.com (requires a You.com API ' +
    'key in Settings → API Keys). Returns web and news results with ' +
    'snippet text. Use {{query}} as the search term.',
};

const LOGIC_TYPES = [
  'condition',
  'merge',
  'set_variable',
  'route_by_score',
];

/** Identifier validation — must be a valid Python identifier. */
const IDENT_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

function Toggle({ label, checked, onChange, hint }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        marginTop: 8,
        marginBottom: 2,
      }}
    >
      <input
        type="checkbox"
        id={`toggle-${label}`}
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ cursor: 'pointer', accentColor: 'var(--accent-color)' }}
      />
      <label
        htmlFor={`toggle-${label}`}
        style={{
          fontSize: '0.78rem',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {label}
        {hint && (
          <span
            style={{
              display: 'block',
              fontSize: '0.7rem',
              color: 'var(--text-secondary)',
              opacity: 0.75,
              lineHeight: 1.35,
              marginTop: 1,
            }}
          >
            {hint}
          </span>
        )}
      </label>
    </div>
  );
}

function TextInput({ value, onChange, placeholder, pattern }) {
  const invalid = pattern && value && !pattern.test(value);
  return (
    <input
      className="wf-input"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      style={invalid ? { borderColor: '#ef4444' } : undefined}
    />
  );
}

function TextArea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      className="wf-textarea"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      className="wf-select"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((opt) => (
        <option key={opt} value={opt}>
          {opt}
        </option>
      ))}
    </select>
  );
}

function NumberInput({ value, onChange, min, max, step = 1 }) {
  return (
    <input
      className="wf-input"
      type="number"
      value={value ?? ''}
      min={min}
      max={max}
      step={step}
      onChange={(e) => onChange(Number(e.target.value))}
    />
  );
}

/* ─── Config forms per node type ──────────────────────────── */

function InputConfig({ config, onChange }) {
  const set = (key) => (val) => onChange({ ...config, [key]: val });
  return (
    <>
      <Field label="Variable Name *">
        <TextInput
          value={config.variable_name}
          onChange={set('variable_name')}
          placeholder="e.g. query"
          pattern={IDENT_PATTERN}
        />
      </Field>
      <Field label="Description">
        <TextInput
          value={config.description}
          onChange={set('description')}
          placeholder="What does this input represent?"
        />
      </Field>
      <Field label="Default Value">
        <TextInput
          value={config.default_value}
          onChange={set('default_value')}
          placeholder="Optional pre-filled value"
        />
      </Field>
    </>
  );
}

function OutputConfig({ config, onChange, upstreamVars }) {
  const set = (key) => (val) => onChange({ ...config, [key]: val });
  const suggestedVar = upstreamVars?.[0] || null;
  return (
    <>
      <Field label="Variable Name *">
        <div style={{ display: 'flex', gap: 6 }}>
          <TextInput
            value={config.variable_name}
            onChange={set('variable_name')}
            placeholder="e.g. summary"
            pattern={IDENT_PATTERN}
          />
          {suggestedVar && config.variable_name !== suggestedVar && (
            <button
              type="button"
              className="wf-btn"
              style={{
                padding: '4px 8px',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onClick={() => set('variable_name')(suggestedVar)}
              title={`Sync from upstream: ${suggestedVar}`}
            >
              Sync
            </button>
          )}
        </div>
      </Field>
      <Field label="Description">
        <TextInput
          value={config.description}
          onChange={set('description')}
          placeholder="What does this output capture?"
        />
      </Field>
    </>
  );
}

/** Smart output variable name suggestions per node/tool type. */
const OUTPUT_VAR_DEFAULTS = {
  generalist:     'generalist_output',
  researcher:     'researcher_output',
  evaluator:      'evaluator_output',
  challenger:     'challenger_output',
  extractor:      'extractor_output',
  ddg_search:     'ddg_search_result',
  arxiv_search:   'arxiv_search_result',
  tavily_search:  'tavily_search_result',
  you_search:     'you_search_result',
};

/**
 * Clickable `{{var}}` pills that insert a variable reference into a field.
 * Renders nothing when there are no upstream variables.
 */
function VarChips({ vars, onInsert }) {
  if (!vars || vars.length === 0) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
      <span style={{
        fontSize: '0.68rem',
        color: 'var(--text-secondary)',
        alignSelf: 'center',
        flexShrink: 0,
      }}>
        Insert:
      </span>
      {vars.map((v) => (
        <button
          key={v}
          type="button"
          onClick={() => onInsert(`{{${v}}}`)}
          title={`Insert {{${v}}}`}
          style={{
            fontSize: '0.68rem',
            padding: '2px 7px',
            border: '1px solid var(--primary-color)',
            borderRadius: 99,
            background: 'transparent',
            color: 'var(--primary-color)',
            cursor: 'pointer',
            fontFamily: 'monospace',
            lineHeight: 1.5,
          }}
        >
          {`{{${v}}}`}
        </button>
      ))}
    </div>
  );
}

const ALL_OUTPUT_VAR_DEFAULTS = new Set([
  'agent_output', 'tool_output', ...Object.values(OUTPUT_VAR_DEFAULTS),
]);

/**
 * Build the sub-agent panel's provider dropdown: the session default, the
 * platform, the disable sentinel, then the user's configured providers
 * (workflowProviderOptions).
 *
 * @param {object} providerConfigs - Raw `provider_configs` from the API.
 * @returns {Array<{value:string, label:string, provider:string|null, configIndex:number|null}>}
 */
function buildProviderOptions(providerConfigs) {
  return [
    {
      value: '',
      label: '(use session default)',
      provider: null,
      configIndex: null,
    },
    STACKFORM_PROVIDER_OPTION,
    {
      value: DISABLED_PROVIDER,
      label: 'None (disabled for this node)',
      provider: DISABLED_PROVIDER,
      configIndex: null,
    },
    ...buildConfiguredProviderOptions(providerConfigs),
  ];
}

const SUB_AGENT_NAMES = ['aeri', 'capo', 'cardian'];

/**
 * Collapsible override panel for a single sub-agent.
 *
 * Provider options are built dynamically from the user's configured
 * provider_configs so that only providers with an active API key appear.
 * Provider and config_index are combined into a single select to match the
 * UX in LLMSettingsSection (e.g. "Azure (2) — My Key").
 * The model field is the shared ModelSelect, listing what Settings and the
 * left panel list; with no provider picked it stays free text, a model
 * on the session-default provider.
 *
 * @param {string}   props.name              - Sub-agent name.
 * @param {object}   props.override          - Current {provider,model,config_index,persona_id}.
 * @param {Function} props.onChange          - Called with updated override object.
 * @param {Function} props.onApplyToAll      - Called when "Apply to all" is clicked.
 * @param {Array}    props.personas          - [{id, title}] list.
 * @param {object}   props.defaultAssignment - DB row ({provider,model_name,persona_id})
 *   shown as a "Session default" hint when the panel is expanded.
 * @param {object}   props.providerConfigs   - Raw provider_configs from the API.
 * @param {object}   [props.modelLists]      - What model fields list
 *   (useModelSettings().modelLists).
 */
function SubAgentPanel({
  name,
  override,
  onChange,
  onApplyToAll,
  personas,
  defaultAssignment,
  providerConfigs,
  modelLists = {},
}) {
  const ov = useMemo(() => override || {}, [override]);
  const hasOverride = !!(ov.provider || ov.model || ov.persona_id);
  const [open, setOpen] = useState(hasOverride);

  /* Open the panel when override data arrives after initial mount. */
  useEffect(() => {
    if (hasOverride) setOpen(true);
  }, [hasOverride]);

  /* Build the combined provider+config_index dropdown options. */
  const providerOptions = useMemo(
    () => buildProviderOptions(providerConfigs),
    [providerConfigs],
  );

  /*
   * Derive the combined select value from the stored provider + config_index.
   * e.g. provider='azure', config_index=2  →  'azure_2'
   *      provider='anthropic', config_index=1  →  'anthropic'
   */
  const currentProviderValue = useMemo(() => {
    if (!ov.provider) return '';
    return ov.config_index && ov.config_index > 1
      ? `${ov.provider}_${ov.config_index}`
      : ov.provider;
  }, [ov.provider, ov.config_index]);

  /* When the combined select changes, split back to provider + config_index. */
  const handleProviderChange = useCallback(
    (value) => {
      if (value === DISABLED_PROVIDER) {
        // Disable this sub-agent for this node: drop its model/config/persona
        // so the slot carries only the disable sentinel.
        onChange({
          ...ov,
          provider: DISABLED_PROVIDER,
          model: null,
          config_index: null,
          persona_id: null,
        });
        return;
      }
      const opt = providerOptions.find((o) => o.value === value);
      if (!opt || !opt.provider) {
        onChange({ ...ov, provider: null, config_index: null });
      } else {
        // Auto-select the provider's recommended model (matching the left
        // panel and Model Defaults tab) so the override isn't left blank.
        // A provider whose list is empty (Azure or AWS with no saved
        // names, or a local key whose device lists none) is left blank
        // for the user to fill, which is the desired behaviour.
        const recommendedModel = defaultModelFor(opt.value, modelLists) || null;
        onChange({
          ...ov,
          provider: opt.provider,
          config_index: opt.configIndex ?? 1,
          model: recommendedModel,
        });
      }
    },
    [providerOptions, ov, onChange, modelLists],
  );

  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            background: 'none',
            border: 'none',
            padding: '3px 0',
            cursor: 'pointer',
            fontSize: '0.74rem',
            color: hasOverride
              ? 'var(--accent-color)'
              : 'var(--text-secondary)',
            fontWeight: hasOverride ? 600 : 400,
          }}
        >
          <span
            style={{
              display: 'inline-block',
              transform: open ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.15s',
              fontSize: '0.62rem',
            }}
          >
            ▶
          </span>
          {name}
          {hasOverride && (
            <span
              style={{
                fontSize: '0.65rem',
                background: 'var(--accent-color)',
                color: '#fff',
                borderRadius: 99,
                padding: '1px 5px',
              }}
            >
              active
            </span>
          )}
        </button>
        <button
          type="button"
          onClick={onApplyToAll}
          disabled={!hasOverride}
          title={
            hasOverride
              ? `Copy ${name}'s settings to aeri, capo, and cardian`
              : 'No active settings to copy'
          }
          style={{
            background: 'none',
            border: '1px solid var(--border-color)',
            borderRadius: 4,
            padding: '2px 7px',
            fontSize: '0.65rem',
            cursor: hasOverride ? 'pointer' : 'not-allowed',
            color: 'var(--text-secondary)',
            whiteSpace: 'nowrap',
            opacity: hasOverride ? 1 : 0.38,
          }}
        >
          Apply to all
        </button>
      </div>

      {open && (
        <div
          style={{
            marginTop: 4,
            paddingLeft: 10,
            borderLeft: '2px solid rgba(59,130,246,0.2)',
          }}
        >
          {/* Session default hint */}
          {defaultAssignment && (
            <div
              style={{
                fontSize: '0.7rem',
                color: 'var(--text-secondary)',
                background: 'var(--hover-color)',
                border: '1px solid var(--border-color)',
                borderRadius: 4,
                padding: '4px 8px',
                marginBottom: 8,
                lineHeight: 1.45,
              }}
            >
              <strong>Session default:</strong>{' '}
              {defaultAssignment.provider || 'stackform'}
              {defaultAssignment.model_name
                ? ` · ${defaultAssignment.model_name}`
                : ''}
            </div>
          )}

          {/* Combined provider + config_index select */}
          <Field label="Provider">
            {providerOptions.length <= 1 ? (
              <div
                style={{
                  fontSize: '0.75rem',
                  color: 'var(--text-secondary)',
                  padding: '6px 0',
                }}
              >
                No providers configured. Add API keys in Settings.
              </div>
            ) : (
              <select
                className="wf-select"
                value={currentProviderValue}
                onChange={(e) => handleProviderChange(e.target.value)}
              >
                {providerOptions.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Model">
            {ov.provider && ov.provider !== DISABLED_PROVIDER ? (
              <ModelSelect
                providerKey={currentProviderValue}
                value={ov.model || ''}
                onChange={(model) => onChange({ ...ov, model: model || null })}
                modelLists={modelLists}
                ariaLabel={`${name} model`}
                listId={`wf-model-list-${name}`}
                selectProps={{ className: 'wf-select' }}
                inputProps={{ className: 'wf-input' }}
              />
            ) : (
              <input
                className="wf-input"
                aria-label={`${name} model`}
                value={ov.model || ''}
                disabled={ov.provider === DISABLED_PROVIDER}
                onChange={(e) =>
                  onChange({
                    ...ov,
                    model: e.target.value || null,
                  })
                }
                placeholder={
                  ov.provider === DISABLED_PROVIDER
                    ? 'Agent disabled for this node'
                    : 'leave blank to use session default'
                }
              />
            )}
            {ov.provider &&
              ov.provider !== DISABLED_PROVIDER &&
              ov.provider !== STACKFORM_PROVIDER_OPTION.provider &&
              !ov.model && (
                <FieldAlert>
                  Choose a model for this provider. The run won&apos;t start
                  without one.
                </FieldAlert>
              )}
          </Field>

          <Field label="Persona">
            <select
              className="wf-select"
              value={ov.persona_id || ''}
              disabled={ov.provider === DISABLED_PROVIDER}
              onChange={(e) =>
                onChange({
                  ...ov,
                  persona_id: e.target.value || null,
                })
              }
            >
              <option value="">(use session default)</option>
              {(personas || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )}
    </div>
  );
}

/**
 * Collapsible per-sub-agent provider / model / persona override section.
 *
 * @param {object}   props
 * @param {object}   props.config                 - Node config object.
 * @param {Function} props.onChange               - Full config replacement callback.
 * @param {Array}    props.personas               - [{id, title}] list for the dropdown.
 * @param {object}   props.agentModelAssignments  - DB defaults keyed by sub-agent name.
 * @param {object}   props.providerConfigs        - Raw provider_configs from the API.
 */
function SubAgentOverrides({
  config,
  onChange,
  personas,
  agentModelAssignments,
  providerConfigs,
  modelLists,
}) {
  const subAgents = config.sub_agents || {};
  const hasAnyOverride = SUB_AGENT_NAMES.some((name) => {
    const ov = subAgents[name];
    return ov && (ov.provider || ov.model || ov.persona_id);
  });
  const [open, setOpen] = useState(hasAnyOverride);

  // Mirror the backend AgentNodeConfig validator: a node that forces every
  // sub-agent off (each either disabled outright or left to a node-level
  // "none" with no real-provider re-enable) leaves the pool empty and is
  // rejected on save. Warn inline so the user catches it before saving.
  const nodeDisabled =
    String(config.provider || '').trim().toLowerCase() === DISABLED_PROVIDER;
  const allSubAgentsDisabled = SUB_AGENT_NAMES.every((name) => {
    const ov = subAgents[name];
    const ovProvider = String(ov?.provider || '').trim().toLowerCase();
    if (ovProvider === DISABLED_PROVIDER) return true;
    if (ov?.provider) return false; // a real provider re-enables the slot
    return nodeDisabled;
  });

  const handleSubAgentChange = (name, updated) => {
    onChange({
      ...config,
      sub_agents: { ...subAgents, [name]: updated },
    });
  };

  const handleApplyToAll = (sourceName) => {
    const source = subAgents[sourceName] || {};
    const newSubAgents = {};
    SUB_AGENT_NAMES.forEach((n) => {
      newSubAgents[n] = { ...source };
    });
    onChange({ ...config, sub_agents: newSubAgents });
  };

  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'none',
          border: 'none',
          padding: '4px 0',
          cursor: 'pointer',
          fontSize: '0.78rem',
          color: hasAnyOverride
            ? 'var(--accent-color)'
            : 'var(--text-secondary)',
          fontWeight: 600,
        }}
      >
        <span
          style={{
            display: 'inline-block',
            transform: open ? 'rotate(90deg)' : 'none',
            transition: 'transform 0.15s',
            fontSize: '0.65rem',
          }}
        >
          ▶
        </span>
        Subagent Overrides
        {hasAnyOverride && (
          <span
            style={{
              fontSize: '0.68rem',
              background: 'var(--accent-color)',
              color: '#fff',
              borderRadius: 99,
              padding: '1px 6px',
              marginLeft: 2,
            }}
          >
            active
          </span>
        )}
      </button>

      {allSubAgentsDisabled && (
        <div
          role="alert"
          style={{
            marginTop: 6,
            fontSize: '0.72rem',
            color: '#b45309',
            background: 'rgba(245,158,11,0.12)',
            border: '1px solid rgba(245,158,11,0.45)',
            borderRadius: 4,
            padding: '5px 8px',
            lineHeight: 1.4,
          }}
        >
          ⚠ All three sub-agents are disabled — this node has no agent to run
          and will be rejected on save. Enable at least one (set its provider).
        </div>
      )}

      {open && (
        <div
          style={{
            marginTop: 6,
            paddingLeft: 10,
            borderLeft: '2px solid rgba(59,130,246,0.25)',
          }}
        >
          {SUB_AGENT_NAMES.map((name) => (
            <SubAgentPanel
              key={name}
              name={name}
              override={subAgents[name]}
              onChange={(updated) => handleSubAgentChange(name, updated)}
              onApplyToAll={() => handleApplyToAll(name)}
              personas={personas}
              defaultAssignment={agentModelAssignments?.[name]}
              providerConfigs={providerConfigs}
              modelLists={modelLists}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AgentConfig({
  config,
  onChange,
  upstreamVars,
  personas,
  agentModelAssignments,
  providerConfigs,
  modelLists,
  activeProfileName,
  profiles,
  workflowProfile,
  onWorkflowProfileChange,
  modelSlots,
  modelSlotsError,
}) {
  const set = (key) => (val) => onChange({ ...config, [key]: val });
  const suggestedVar =
    OUTPUT_VAR_DEFAULTS[config.agent_type] || 'agent_output';
  // The node's model slots and whether it has sub-agents both come from
  // the backend's slot table (useModelSlots), not from the agent type.
  // Until the table loads that isn't known, so the sub-agent panels wait;
  // if it never loads they show, as before model slots.
  const nodeSlots = slotsForAgentType(modelSlots, config.agent_type);
  const showSubAgents = modelSlots !== null
    ? !isSingleCallAgentType(modelSlots, config.agent_type)
    : Boolean(modelSlotsError);

  const handleAgentTypeChange = (newType) => {
    const newDefault = OUTPUT_VAR_DEFAULTS[newType] || 'agent_output';
    const shouldUpdate = ALL_OUTPUT_VAR_DEFAULTS.has(
      config.output_variable
    );
    onChange({
      ...fitConfigToAgentType(config, newType, modelSlots),
      agent_type: newType,
      output_variable: shouldUpdate
        ? newDefault
        : config.output_variable,
    });
  };
  return (
    <>
      <Field label="Agent Type *">
        <Select
          value={config.agent_type}
          onChange={handleAgentTypeChange}
          options={AGENT_TYPES}
        />
        <TypeHint text={AGENT_DESCRIPTIONS[config.agent_type]} />
      </Field>
      {onWorkflowProfileChange && (
        <Field label="Model profile (this workflow's runs)">
          <select
            className="wf-select"
            value={workflowProfile || ''}
            onChange={(e) =>
              onWorkflowProfileChange(e.target.value || null)
            }
          >
            <option value="">
              {`Use active profile${
                activeProfileName ? ` (${activeProfileName})` : ''
              }`}
            </option>
            {(profiles || []).map((p) => (
              <option key={p.profile_name} value={p.profile_name}>
                {p.profile_name}
              </option>
            ))}
          </select>
          <TypeHint
            text={
              'Sets which model profile this workflow runs with. Keep it '
              + 'on "active profile" and the workflow uses whichever '
              + 'profile you currently have selected; choose a specific '
              + 'profile to lock this workflow to it regardless. A node\'s '
              + 'own model and subagent overrides below still apply on top.'
            }
          />
        </Field>
      )}
      {modelSlots === null && !modelSlotsError && (
        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--text-secondary)',
            margin: '6px 0',
          }}
        >
          Loading model options…
        </div>
      )}
      {modelSlotsError && (
        <div
          role="alert"
          style={{ fontSize: '0.72rem', color: '#b45309', margin: '6px 0' }}
        >
          Couldn&apos;t load model options: {modelSlotsError}
        </div>
      )}
      {nodeSlots.map((slot) => {
        const copy = slotCopy(slot.slot);
        return (
          <ModelChoicePicker
            key={slot.slot}
            idPrefix={`wf-slot-${slot.slot}`}
            label={copy.label}
            hint={copy.hint}
            value={config[slot.field] || null}
            onChange={(choice) =>
              onChange({ ...config, [slot.field]: choice })
            }
            defaultChoice={slot.default}
            providerConfigs={providerConfigs}
            modelLists={modelLists}
          />
        );
      })}
      {showSubAgents && (
        <SubAgentOverrides
          config={config}
          onChange={onChange}
          personas={personas}
          agentModelAssignments={agentModelAssignments}
          providerConfigs={providerConfigs}
          modelLists={modelLists}
        />
      )}
      <Field label="Prompt Template *">
        <TextArea
          value={config.prompt_template}
          onChange={set('prompt_template')}
          placeholder={
            'Use {{variable_name}} to inject upstream values.\n' +
            'e.g. Summarise: {{document}}'
          }
          rows={5}
        />
        <VarChips
          vars={upstreamVars}
          onInsert={(token) =>
            set('prompt_template')(
              config.prompt_template
                ? `${config.prompt_template} ${token}`
                : token
            )
          }
        />
      </Field>
      <Field label="Output Variable">
        <div style={{ display: 'flex', gap: 6 }}>
          <TextInput
            value={config.output_variable}
            onChange={set('output_variable')}
            placeholder="agent_output"
            pattern={IDENT_PATTERN}
          />
          {config.output_variable !== suggestedVar && (
            <button
              type="button"
              className="wf-btn"
              style={{
                padding: '4px 8px',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onClick={() => set('output_variable')(suggestedVar)}
              title={`Set to: ${suggestedVar}`}
            >
              Suggest
            </button>
          )}
        </div>
      </Field>
      <Field label="Timeout (s)">
        <NumberInput
          value={config.timeout}
          onChange={set('timeout')}
          min={1}
          max={3600}
          step={1}
        />
      </Field>
      <Toggle
        label="Soft fail"
        checked={config.soft_fail ?? false}
        onChange={set('soft_fail')}
        hint="On failure, write an error note to output_variable and let downstream nodes continue."
      />
      {config.agent_type === 'generalist' && (
        <Toggle
          label="Web search"
          checked={config.web_search ?? false}
          onChange={set('web_search')}
          hint="When on, the generalist fetches live web results before answering. Turn off to answer from training knowledge only (faster, no external calls)."
        />
      )}
      {(config.agent_type === 'researcher' ||
        (config.agent_type === 'generalist' &&
          (config.web_search ?? false))) && (
        <Field label="Search Query Template (optional)">
          <TextArea
            value={config.search_query_template || ''}
            onChange={set('search_query_template')}
            placeholder={
              'Concise query sent to search tools (DuckDuckGo, arXiv, ' +
              'Tavily).\nUse {{variable}} to inject context values.\n' +
              'e.g. {{query}}\n\n' +
              'Leave blank to use the full prompt as the search query.'
            }
            rows={2}
          />
          <VarChips
            vars={upstreamVars}
            onInsert={(token) =>
              set('search_query_template')(
                config.search_query_template
                  ? `${config.search_query_template} ${token}`
                  : token
              )
            }
          />
          <TypeHint
            text={
              config.search_query_template
                ? null
                : 'Tip: without a search query template the full ' +
                  'prompt text is sent to the search engine, which ' +
                  'can hurt result quality when the prompt contains ' +
                  'multi-paragraph context. Set {{query}} here for ' +
                  'best results.'
            }
          />
        </Field>
      )}
    </>
  );
}

function ToolConfig({ config, onChange, upstreamVars }) {
  const set = (key) => (val) => onChange({ ...config, [key]: val });
  const suggestedVar =
    OUTPUT_VAR_DEFAULTS[config.tool_type] || 'tool_output';

  const handleToolTypeChange = (newType) => {
    const newDefault = OUTPUT_VAR_DEFAULTS[newType] || 'tool_output';
    const shouldUpdate = ALL_OUTPUT_VAR_DEFAULTS.has(
      config.output_variable
    );
    onChange({
      ...config,
      tool_type: newType,
      output_variable: shouldUpdate
        ? newDefault
        : config.output_variable,
    });
  };

  const paramsStr = useMemo(
    () => (config.parameters ? JSON.stringify(config.parameters, null, 2) : '{}'),
    [config.parameters],
  );

  const handleParamsChange = (raw) => {
    try {
      const parsed = JSON.parse(raw);
      onChange({ ...config, parameters: parsed });
    } catch (_) {
      /* allow invalid JSON while typing */
    }
  };

  const handleParamVarInsert = (token) => {
    try {
      const parsed = JSON.parse(paramsStr) || {};
      parsed.query = token;
      onChange({ ...config, parameters: parsed });
    } catch (_) {
      onChange({ ...config, parameters: { query: token } });
    }
  };

  return (
    <>
      <Field label="Tool Type *">
        <Select
          value={config.tool_type}
          onChange={handleToolTypeChange}
          options={TOOL_TYPES}
        />
        <TypeHint text={TOOL_DESCRIPTIONS[config.tool_type]} />
      </Field>
      <Field label="Parameters (JSON)">
        <TextArea
          value={paramsStr}
          onChange={handleParamsChange}
          placeholder='{"query": "{{search_term}}"}'
          rows={5}
        />
        <VarChips vars={upstreamVars} onInsert={handleParamVarInsert} />
      </Field>
      <Field label="Output Variable">
        <div style={{ display: 'flex', gap: 6 }}>
          <TextInput
            value={config.output_variable}
            onChange={set('output_variable')}
            placeholder="tool_output"
            pattern={IDENT_PATTERN}
          />
          {config.output_variable !== suggestedVar && (
            <button
              type="button"
              className="wf-btn"
              style={{
                padding: '4px 8px',
                fontSize: '0.72rem',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onClick={() => set('output_variable')(suggestedVar)}
              title={`Set to: ${suggestedVar}`}
            >
              Suggest
            </button>
          )}
        </div>
      </Field>
      <Field label="Timeout (s)">
        <NumberInput
          value={config.timeout}
          onChange={set('timeout')}
          min={1}
          max={300}
          step={1}
        />
      </Field>
      <Toggle
        label="Soft fail (on by default)"
        checked={config.soft_fail ?? true}
        onChange={set('soft_fail')}
        hint="On failure, write an error note to output_variable and let downstream nodes continue."
      />
    </>
  );
}

function ConditionSubConfig({ condition, onChange, allNodes }) {
  const set = (key) => (val) => onChange({ ...condition, [key]: val });
  const nodeOptions = (allNodes || []).filter(
    (n) => n.data?.config?.node_type !== 'logic',
  );
  return (
    <>
      <Field label="Expression *">
        <TextInput
          value={condition?.expression}
          onChange={set('expression')}
          placeholder='e.g. score > 0.8'
        />
      </Field>
      <Field label="True Branch">
        <select
          className="wf-select"
          value={condition?.true_next || ''}
          onChange={(e) => set('true_next')(e.target.value)}
        >
          <option value="">— none —</option>
          {nodeOptions.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data?.label || n.id}
            </option>
          ))}
        </select>
      </Field>
      <Field label="False Branch">
        <select
          className="wf-select"
          value={condition?.false_next || ''}
          onChange={(e) => set('false_next')(e.target.value)}
        >
          <option value="">— none —</option>
          {nodeOptions.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data?.label || n.id}
            </option>
          ))}
        </select>
      </Field>
    </>
  );
}

function SetVarSubConfig({ setVariable, onChange }) {
  const set = (key) => (val) => onChange({ ...setVariable, [key]: val });
  return (
    <>
      <Field label="Variable Name *">
        <TextInput
          value={setVariable?.variable_name}
          onChange={set('variable_name')}
          placeholder="result"
          pattern={IDENT_PATTERN}
        />
      </Field>
      <Field label="Value Expression *">
        <TextInput
          value={setVariable?.value_expression}
          onChange={set('value_expression')}
          placeholder="agent_output.strip()"
        />
      </Field>
    </>
  );
}

const ROUTE_SEARCH_TOOLS = [
  'ddg_search',
  'arxiv_search',
  'tavily_search',
  'you_search',
];

const ROUTE_SEARCH_TOOL_LABELS = {
  ddg_search: 'DuckDuckGo (free, no key)',
  arxiv_search: 'arXiv (free, academic)',
  tavily_search: 'Tavily (requires API key)',
  you_search: 'You.com (requires API key)',
};

function RouteByScoreSubConfig({
  routeByScore,
  onChange,
  allNodes,
  upstreamVars,
}) {
  const rbs = routeByScore || {};
  const set = (key) => (val) => onChange({ ...rbs, [key]: val });
  const nodeOptions = (allNodes || []).filter(
    (n) => n.data?.config?.node_type !== 'logic',
  );
  return (
    <>
      <Field label="Score Variable *">
        <TextInput
          value={rbs.score_variable}
          onChange={set('score_variable')}
          placeholder="e.g. confidence_score"
          pattern={IDENT_PATTERN}
        />
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-secondary)',
            marginTop: 3,
          }}
        >
          Context variable produced by an upstream evaluator node
          (numeric 0.0 – 1.0).
        </div>
        <VarChips vars={upstreamVars} onInsert={set('score_variable')} />
      </Field>

      <Field label="Threshold (0.0 – 1.0)">
        <NumberInput
          value={rbs.threshold ?? 0.7}
          onChange={set('threshold')}
          min={0}
          max={1}
          step={0.05}
        />
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-secondary)',
            marginTop: 3,
          }}
        >
          Scores ≥ threshold → high-confidence branch (no search).
          Scores below → search runs, then retry branch.
        </div>
      </Field>

      <Field label="Search Tool">
        <select
          className="wf-select"
          value={rbs.search_tool || 'ddg_search'}
          onChange={(e) => set('search_tool')(e.target.value)}
        >
          {ROUTE_SEARCH_TOOLS.map((t) => (
            <option key={t} value={t}>
              {ROUTE_SEARCH_TOOL_LABELS[t] || t}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Search Query Template *">
        <TextInput
          value={rbs.search_query_template}
          onChange={set('search_query_template')}
          placeholder="e.g. {{query}}"
        />
        <VarChips
          vars={upstreamVars}
          onInsert={(token) =>
            set('search_query_template')(
              rbs.search_query_template
                ? `${rbs.search_query_template} ${token}`
                : token,
            )
          }
        />
      </Field>

      <Field label="Search Output Variable">
        <TextInput
          value={rbs.search_output_variable}
          onChange={set('search_output_variable')}
          placeholder="route_search_result"
          pattern={IDENT_PATTERN}
        />
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-secondary)',
            marginTop: 3,
          }}
        >
          Available as{' '}
          <code>
            {`{{${rbs.search_output_variable || 'route_search_result'}}}`}
          </code>{' '}
          in the retry branch.
        </div>
      </Field>

      <Field label="High-Confidence Branch (score ≥ threshold)">
        <select
          className="wf-select"
          value={rbs.high_next || ''}
          onChange={(e) => set('high_next')(e.target.value)}
        >
          <option value="">— none —</option>
          {nodeOptions.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data?.label || n.id}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Retry Branch (score < threshold)">
        <select
          className="wf-select"
          value={rbs.low_next || ''}
          onChange={(e) => set('low_next')(e.target.value)}
        >
          <option value="">— none —</option>
          {nodeOptions.map((n) => (
            <option key={n.id} value={n.id}>
              {n.data?.label || n.id}
            </option>
          ))}
        </select>
        <div
          style={{
            fontSize: '0.7rem',
            color: 'var(--text-secondary)',
            marginTop: 3,
          }}
        >
          This node receives the search results via{' '}
          <code>
            {`{{${rbs.search_output_variable || 'route_search_result'}}}`}
          </code>
          .
        </div>
      </Field>
    </>
  );
}

function LogicConfig({
  config,
  onChange,
  allNodes,
  upstreamVars,
}) {
  const set = (key) => (val) => onChange({ ...config, [key]: val });

  const handleLogicTypeChange = (lt) => {
    const next = { ...config, logic_type: lt };
    if (lt === 'condition' && !next.condition) {
      next.condition = {
        expression: '',
        true_next: '',
        false_next: '',
      };
    }
    if (lt === 'set_variable' && !next.set_variable) {
      next.set_variable = { variable_name: '', value_expression: '' };
    }
    if (lt === 'route_by_score' && !next.route_by_score) {
      next.route_by_score = {
        score_variable: '',
        threshold: 0.7,
        search_tool: 'ddg_search',
        search_query_template: '',
        search_output_variable: 'route_search_result',
        high_next: '',
        low_next: '',
      };
    }
    onChange(next);
  };

  const LOGIC_TYPE_LABELS = {
    condition: 'Condition',
    merge: 'Merge',
    set_variable: 'Set Variable',
    route_by_score: 'Route by Confidence Score',
  };

  return (
    <>
      <Field label="Logic Type *">
        <select
          className="wf-select"
          value={config.logic_type || ''}
          onChange={(e) => handleLogicTypeChange(e.target.value)}
        >
          {LOGIC_TYPES.map((lt) => (
            <option key={lt} value={lt}>
              {LOGIC_TYPE_LABELS[lt] || lt}
            </option>
          ))}
        </select>
      </Field>
      {config.logic_type === 'condition' && (
        <ConditionSubConfig
          condition={config.condition || {}}
          onChange={(c) => set('condition')(c)}
          allNodes={allNodes}
        />
      )}
      {config.logic_type === 'set_variable' && (
        <SetVarSubConfig
          setVariable={config.set_variable || {}}
          onChange={(sv) => set('set_variable')(sv)}
        />
      )}
      {config.logic_type === 'route_by_score' && (
        <RouteByScoreSubConfig
          routeByScore={config.route_by_score || {}}
          onChange={(rbs) => set('route_by_score')(rbs)}
          allNodes={allNodes}
          upstreamVars={upstreamVars}
        />
      )}
      {config.logic_type === 'merge' && (
        <p
          style={{
            fontSize: '0.8rem',
            color: 'var(--text-secondary)',
            margin: 0,
          }}
        >
          Merge waits for all incoming branches and combines their
          variables — no further configuration needed.
        </p>
      )}
    </>
  );
}

/* ─── Main panel ──────────────────────────────────────────── */

const CONFIG_FORMS = {
  input: InputConfig,
  output: OutputConfig,
  agent: AgentConfig,
  tool: ToolConfig,
  logic: LogicConfig,
};

/**
 * Right-side panel that renders the config form for the selected node.
 *
 * @param {object}   props
 * @param {object}   props.node                  - Selected xyflow node (or null).
 * @param {Function} props.onUpdate              - Called with (nodeId, {label, config}).
 * @param {Function} props.onDelete              - Called with (nodeId).
 * @param {Array}    props.allNodes              - All nodes in the graph.
 * @param {Array}    props.edges                 - All edges in the graph.
 * @param {Array}    props.personas              - User's saved personas [{id, title}].
 * @param {object}   props.agentModelAssignments - Current DB agent assignments
 *   keyed by sub-agent name, used to show session defaults in sub-agent panels.
 * @param {object}   props.providerConfigs      - Raw provider_configs from the API,
 *   used to build the dynamic provider dropdown in sub-agent panels.
 * @param {object}   props.toolApiKeys          - Map of tool_name → has_key boolean.
 * @param {boolean}  props.requireProviderKeys  - When true, agent nodes require a
 *   configured provider key; during beta this is false.
 * @param {Array}    [props.modelSlots]         - The served node model slot
 *   table (useModelSlots); null while loading.
 * @param {string}   [props.modelSlotsError]    - Why the table didn't load.
 */
export default function WorkflowNodeConfig({
  node,
  onUpdate,
  onDelete,
  allNodes,
  edges,
  personas,
  agentModelAssignments,
  providerConfigs,
  modelLists,
  activeProfileName,
  profiles,
  workflowProfile,
  onWorkflowProfileChange,
  toolApiKeys,
  requireProviderKeys,
  modelSlots = null,
  modelSlotsError = null,
}) {
  const handleLabelChange = useCallback(
    (val) => {
      if (!node) return;
      onUpdate(node.id, { label: val, config: node.data.config });
    },
    [node, onUpdate],
  );

  const handleConfigChange = useCallback(
    (newConfig) => {
      if (!node) return;
      onUpdate(node.id, { label: node.data.label, config: newConfig });
    },
    [node, onUpdate],
  );

  /*
   * Build richer upstream metadata: node type + the correct variable name.
   * Input nodes expose `variable_name`; every other type exposes
   * `output_variable`.  Filtering out null entries keeps downstream code clean.
   */
  const upstreamNodeInfo = useMemo(() => {
    if (!node || !edges || !allNodes) return [];
    return edges
      .filter((e) => e.target === node.id)
      .map((e) => {
        const src = allNodes.find((n) => n.id === e.source);
        if (!src) return null;
        const cfg = src.data?.config || {};
        const varName =
          cfg.node_type === 'input'
            ? cfg.variable_name || null
            : cfg.output_variable || null;
        return varName ? { nodeType: cfg.node_type || null, varName } : null;
      })
      .filter(Boolean);
  }, [node, edges, allNodes]);

  /* Flat list used by VarChips and tool auto-fill. */
  const upstreamVars = useMemo(
    () => upstreamNodeInfo.map((i) => i.varName),
    [upstreamNodeInfo],
  );

  const handleAutoFill = useCallback(() => {
    if (!node || upstreamNodeInfo.length === 0) return;
    const cfg = node.data?.config || {};
    const nodeType = cfg.node_type;

    if (nodeType === 'agent') {
      const inputVars = upstreamNodeInfo
        .filter((i) => i.nodeType === 'input')
        .map((i) => i.varName);
      const toolVars = upstreamNodeInfo
        .filter((i) => i.nodeType === 'tool')
        .map((i) => i.varName);
      const otherVars = upstreamNodeInfo
        .filter((i) => i.nodeType !== 'input' && i.nodeType !== 'tool')
        .map((i) => i.varName);

      let template;
      if (toolVars.length > 0) {
        /*
         * Tools connected: build "Answer / Context" structure.
         * Query comes from the first input var (or first other var as fallback).
         */
        const queryRef = inputVars[0]
          ? `{{${inputVars[0]}}}`
          : otherVars[0]
          ? `{{${otherVars[0]}}}`
          : '{{query}}';
        const contextRefs = toolVars.map((v) => `{{${v}}}`).join(' ');
        template = `Answer: ${queryRef}\n\nContext: ${contextRefs}`;
      } else if (inputVars.length > 0) {
        /* Only input nodes upstream — simple answer prompt. */
        template = `Answer: {{${inputVars[0]}}}`;
      } else {
        /* Chained agents or other upstream nodes — join as context blocks. */
        template = otherVars.map((v) => `{{${v}}}`).join('\n\n');
      }

      const outputVar =
        OUTPUT_VAR_DEFAULTS[cfg.agent_type] || 'agent_output';
      handleConfigChange({
        ...cfg,
        prompt_template: template,
        output_variable: outputVar,
      });
    } else if (nodeType === 'tool') {
      if (!upstreamVars[0]) return;
      const queryVar = `{{${upstreamVars[0]}}}`;
      const newParams = { ...(cfg.parameters || {}), query: queryVar };
      const outputVar =
        OUTPUT_VAR_DEFAULTS[cfg.tool_type] || 'tool_output';
      handleConfigChange({
        ...cfg,
        parameters: newParams,
        output_variable: outputVar,
      });
    }
  }, [node, upstreamNodeInfo, upstreamVars, handleConfigChange]);

  if (!node) {
    return (
      <div className="wf-config-panel">
        <div className="wf-config-empty">
          Select a node on the canvas to configure.
        </div>
      </div>
    );
  }

  const nodeType = node.data?.config?.node_type || 'input';
  const color = NODE_COLORS[nodeType] || '#888';
  const ConfigForm = CONFIG_FORMS[nodeType];
  const canAutoFill =
    upstreamVars.length > 0 &&
    (nodeType === 'agent' || nodeType === 'tool');

  return (
    <div className="wf-config-panel">
      <div className="wf-config-header">
        <span
          className="wf-config-title"
          style={{ color }}
        >
          {nodeType} node
        </span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {canAutoFill && (
            <button
              className="wf-btn"
              style={{ padding: '3px 8px', fontSize: '0.72rem' }}
              onClick={handleAutoFill}
              title="Auto-fill prompt and output variable from upstream connections"
            >
              ✦ Auto-fill
            </button>
          )}
          <button
            className="wf-btn wf-btn-danger"
            style={{ padding: '3px 10px', fontSize: '0.78rem' }}
            onClick={() => onDelete(node.id)}
          >
            Delete
          </button>
        </div>
      </div>

      {/* API-key warning banners */}
      {nodeType === 'agent' && requireProviderKeys &&
        !Object.keys(providerConfigs || {}).length && (
        <div className="wf-node-key-warning">
          ⚠ A provider API key is required to run this node.
          Add one in <strong>Settings → API Keys</strong>.
        </div>
      )}
      {nodeType === 'tool' && (() => {
        const toolType = node.data?.config?.tool_type;
        /* Map config tool_type to the canonical DB key name used in
         * toolApiKeys (from ALLOWED_TOOL_NAMES: 'tavily', 'you').
         * ddg_search and arxiv_search need no API key — skip them. */
        const TOOL_KEY_MAP = {
          tavily_search: 'tavily',
          you_search: 'you',
        };
        const keyName = TOOL_KEY_MAP[toolType];
        if (!keyName) return null;
        if (toolApiKeys?.[keyName] === false) {
          const names = { tavily: 'Tavily', you: 'You Search' };
          return (
            <div className="wf-node-key-warning">
              ⚠ A <strong>{names[keyName]}</strong> API key
              is required to run this node.
              Add one in <strong>Settings → API Keys</strong>.
            </div>
          );
        }
        return null;
      })()}

      <div className="wf-config-body">
        <Field label="Node Label">
          <TextInput
            value={node.data?.label}
            onChange={handleLabelChange}
            placeholder="Descriptive name"
          />
        </Field>

        {ConfigForm && (
          <ConfigForm
            key={node.id}
            config={node.data?.config || {}}
            onChange={handleConfigChange}
            allNodes={allNodes || []}
            upstreamVars={upstreamVars}
            personas={personas || []}
            agentModelAssignments={agentModelAssignments || {}}
            providerConfigs={providerConfigs || {}}
            modelLists={modelLists || {}}
            activeProfileName={activeProfileName}
            profiles={profiles}
            workflowProfile={workflowProfile}
            onWorkflowProfileChange={onWorkflowProfileChange}
            modelSlots={modelSlots}
            modelSlotsError={modelSlotsError}
          />
        )}

        <div
          style={{
            fontSize: '0.72rem',
            color: 'var(--text-secondary)',
            marginTop: 4,
            lineHeight: 1.5,
          }}
        >
          Node ID: <code>{node.id}</code>
        </div>
      </div>
    </div>
  );
}
